import { memo, useCallback, useMemo, useEffect } from "react";
import { X, GripVertical, Plus, ArrowRight, AlertTriangle } from "lucide-react";

const normalize = (w) => w.trim().toLowerCase();

const validateRelier = (pairs, ciblesLeurres = []) => {
    const errors = [];
    const filled = pairs.filter(p => p.source?.trim() || p.target?.trim());

    if (filled.length < 2) {
        errors.push('Il faut au moins 2 paires.');
        return errors;
    }

    // Paires incomplètes
    const incomplete = pairs.filter(p => (p.source?.trim() && !p.target?.trim()) || (!p.source?.trim() && p.target?.trim()));
    if (incomplete.length > 0) {
        errors.push('Chaque paire doit avoir une source et une cible.');
    }

    // Sources en double
    const sources = pairs.map(p => normalize(p.source || '')).filter(s => s);
    if (new Set(sources).size < sources.length) {
        errors.push('Une même source apparaît plusieurs fois.');
    }

    // Cibles en double — leurres inclus : un leurre égal à une vraie cible rend
    // l'appariement ambigu (l'élève ne peut pas savoir laquelle relier)
    const leurres = ciblesLeurres.map(l => normalize(l?.target || '')).filter(t => t);
    const targets = [...pairs.map(p => normalize(p.target || '')).filter(t => t), ...leurres];
    if (new Set(targets).size < targets.length) {
        errors.push('Une même cible apparaît plusieurs fois (cibles en trop comprises).');
    }

    // Leurre vide
    if (ciblesLeurres.some(l => l && !String(l.target || '').trim())) {
        errors.push('Une cible en trop est vide.');
    }

    // Source identique à une cible
    const sourcesSet = new Set(sources);
    const conflict = targets.find(t => sourcesSet.has(t));
    if (conflict) {
        errors.push(`"${conflict}" est à la fois source et cible.`);
    }

    return errors;
};

const DEFAULT_PAIRS = [{ source: '', target: '' }];

const RelierEditor = memo(function RelierEditor({ data, onDataChange, onValidChange }) {
    const pairs = data?.pairs || DEFAULT_PAIRS;
    // cibles_leurres : pas encore d'UI d'édition (V1.5) mais le champ est validé
    // et préservé (les updates font { ...prev } — un exercice généré avec leurres
    // n'est pas amputé en passant par cet éditeur)
    const ciblesLeurres = useMemo(() => data?.cibles_leurres || [], [data]);

    const errors = useMemo(() => validateRelier(pairs, ciblesLeurres), [pairs, ciblesLeurres]);
    const isValid = errors.length === 0;

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    const updatePair = useCallback((index, field, value) => {
        onDataChange(prev => {
            const next = [...(prev?.pairs || [])];
            next[index] = { ...next[index], [field]: value };
            return { ...prev, pairs: next };
        });
    }, [onDataChange]);

    const addPair = useCallback(() => {
        onDataChange(prev => ({
            ...prev,
            pairs: [...(prev?.pairs || []), { source: '', target: '' }],
        }));
    }, [onDataChange]);

    const removePair = useCallback((index) => {
        onDataChange(prev => ({
            ...prev,
            pairs: (prev?.pairs || []).filter((_, i) => i !== index),
        }));
    }, [onDataChange]);

    return (
        <div className="space-y-1.5">
            {pairs.map((pair, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 group">
                    <GripVertical size={12} className="text-gray-300 flex-shrink-0 cursor-grab hidden sm:block" />
                    <div className="flex-1 flex items-center gap-2 px-2 sm:px-2.5 py-1.5 sm:py-2 rounded-lg border border-blue-900/20 bg-blue-50/50">
                        <input
                            type="text"
                            value={pair.source}
                            onChange={(e) => updatePair(i, 'source', e.target.value)}
                            placeholder="Source"
                            className="flex-1 bg-transparent text-xs sm:text-sm text-blue-900 font-medium placeholder-gray-400 focus:outline-none min-w-0"
                        />
                    </div>
                    <ArrowRight size={14} className="text-gray-400 flex-shrink-0 self-center rotate-90 sm:rotate-0" />
                    <div className="flex-1 flex items-center gap-2 px-2 sm:px-2.5 py-1.5 sm:py-2 rounded-lg border border-gray-200">
                        <input
                            type="text"
                            value={pair.target}
                            onChange={(e) => updatePair(i, 'target', e.target.value)}
                            placeholder="Cible"
                            className="flex-1 bg-transparent text-xs sm:text-sm text-gray-700 placeholder-gray-400 focus:outline-none min-w-0"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => removePair(i)}
                        className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                    >
                        <X size={12} />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={addPair}
                className="w-full flex items-center justify-center gap-1.5 py-2 border-[1.5px] border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:border-gray-300 transition"
            >
                <Plus size={12} />
                Ajouter une paire
            </button>

            {ciblesLeurres.length > 0 ? (
                <div className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-[11px] leading-snug text-slate-600">
                        Cet exercice contient {ciblesLeurres.length} cible{ciblesLeurres.length > 1 ? 's' : ''} en trop
                        (sans correspondance) : {ciblesLeurres.map(l => `« ${l.target} »`).join(', ')}.
                        Elles seront mélangées aux vraies cibles.
                    </p>
                </div>
            ) : null}

            <div className="flex items-center justify-between">
                <p className="text-[11px] text-gray-400">
                    {ciblesLeurres.length > 0
                        ? "Chaque source sera reliée à sa cible ; les cibles en trop ne se relient à rien. L'élève verra toutes les cibles mélangées."
                        : "Chaque source sera reliée à sa cible. L'élève verra les cibles mélangées."}
                </p>
                {pairs.filter(p => p.source?.trim() && p.target?.trim()).length > 0 && (
                    <span className={`text-[10px] font-medium ${pairs.filter(p => p.source?.trim() && p.target?.trim()).length >= 2 ? 'text-emerald-600' : 'text-amber-500'}`}>
                        {pairs.filter(p => p.source?.trim() && p.target?.trim()).length} paire{pairs.filter(p => p.source?.trim() && p.target?.trim()).length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {errors.length > 0 && pairs.some(p => p.source?.trim() || p.target?.trim()) && (
                <div className="space-y-1">
                    {errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />
                            <p className="text-[11px] text-amber-700">{err}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default RelierEditor;
